import { useEffect, useState } from 'react';

/**
 * v5.7.7 — Service Worker update banner.
 *
 * Сценарий: при deploy нового релиза SW переcкачается браузером, перейдёт
 * в `waiting` state. Без явного вмешательства пользователь не увидит
 * обновлений, пока не закроет ВСЕ табы.
 *
 * Этот компонент:
 *   1. Регистрируется на `controllerchange` события `navigator.serviceWorker`
 *      и `message` события с `type: 'SW_UPDATED'` (отправлен из sw.js
 *      `activate`).
 *   2. Периодически вызывает `registration.update()` чтобы триггерить
 *      проверку нового SW даже без перезагрузки (focus event + interval).
 *   3. Когда новая версия готова — показывает баннер "Доступно обновление".
 *   4. По клику — отправляет `SKIP_WAITING` в SW и делает `location.reload()`.
 *
 * Mount'ить один раз в `App` (root level).
 */
export default function SWUpdateBanner() {
  const [waitingReg, setWaitingReg] = useState<ServiceWorkerRegistration | null>(null);
  const [updatedNotice, setUpdatedNotice] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let reg: ServiceWorkerRegistration | null = null;
    let cancelled = false;
    let updateInterval: ReturnType<typeof setInterval> | null = null;

    const onWaitingFound = (r: ServiceWorkerRegistration) => {
      if (cancelled) return;
      if (r.waiting) {
        setWaitingReg(r);
      }
    };

    const handleRegistration = (r: ServiceWorkerRegistration) => {
      reg = r;
      onWaitingFound(r);
      // Listen for newly-installed-and-waiting workers.
      r.addEventListener('updatefound', () => {
        const newSW = r.installing;
        if (!newSW) return;
        newSW.addEventListener('statechange', () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            // New SW installed and waiting. Show banner.
            setWaitingReg(r);
          }
        });
      });

      // Periodic update check (every 10 min) — даже если юзер сидит на одной странице.
      updateInterval = setInterval(() => {
        r.update().catch(() => undefined);
      }, 10 * 60 * 1000);
    };

    navigator.serviceWorker.getRegistration().then((r) => {
      if (r) handleRegistration(r);
    });

    // Если SW зарегистрируется ПОСЛЕ маунта (race с main.tsx) — fallback
    // на ready promise.
    navigator.serviceWorker.ready.then(handleRegistration).catch(() => undefined);

    // controllerchange = новый SW взял control (после skipWaiting+claim).
    // Это значит уже произошёл "soft swap" — для безопасности перезагрузим.
    // Но не перезагружаем если просто маунт (controller уже был с самого начала).
    let firstControllerSeen = false;
    const onControllerChange = () => {
      if (firstControllerSeen) {
        window.location.reload();
      } else {
        firstControllerSeen = true;
      }
    };
    if (navigator.serviceWorker.controller) firstControllerSeen = true;
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Сообщение от SW об активации.
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === 'SW_UPDATED') {
        setUpdatedNotice(true);
        // Краткое уведомление: чате уже на свежем SW, через 5s банер скрываем.
        setTimeout(() => setUpdatedNotice(false), 5000);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    // На focus вкладки тоже проверяем апдейт — юзер мог уйти и вернуться.
    const onFocus = () => reg?.update().catch(() => undefined);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if (updateInterval) clearInterval(updateInterval);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      navigator.serviceWorker.removeEventListener('message', onMessage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!waitingReg && !updatedNotice) return null;

  if (updatedNotice) {
    return (
      <div className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg bg-green-700 text-white shadow-2xl px-4 py-3 text-sm flex items-center gap-3">
        <span>✓ Приложение обновлено до последней версии</span>
      </div>
    );
  }

  const handleReload = () => {
    if (waitingReg?.waiting) {
      waitingReg.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // No waiting worker — просто reload.
      window.location.reload();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg bg-brand-700 text-white shadow-2xl px-4 py-3 text-sm flex items-center gap-3">
      <span className="flex-1">Доступно обновление приложения</span>
      <button
        onClick={handleReload}
        className="px-3 py-1.5 rounded-md bg-white text-brand-700 font-semibold hover:bg-brand-50 transition-colors"
      >
        Обновить
      </button>
    </div>
  );
}
